import { useEffect, useState } from "react";
import { api } from "./lib/api";

type Preferences = {
  register: "practical" | "classic";
  directness: number;
  challenge_level: number;
  verbosity: number;
  evidence_detail: number;
};
type SharedPhrase = {
  id: string;
  phrase: string;
  meaning: string;
  type: string;
  reuse_policy: string;
  status: "candidate" | "active";
  user_confirmed: boolean;
};

const levels = {
  directness: [
    { label: "Gentler", value: 30 },
    { label: "Balanced", value: 50 },
    { label: "Direct", value: 75 },
  ],
  challenge: [
    { label: "Supportive", value: 30 },
    { label: "Balanced", value: 50 },
    { label: "Challenge me", value: 75 },
  ],
  verbosity: [
    { label: "Short", value: 25 },
    { label: "Balanced", value: 50 },
    { label: "Detailed", value: 80 },
  ],
  evidence: [
    { label: "Light", value: 25 },
    { label: "Balanced", value: 50 },
    { label: "Show the trail", value: 80 },
  ],
} as const;

function nearestLabel(value: number, choices: readonly { label: string; value: number }[]) {
  return choices.reduce((best, choice) => Math.abs(choice.value - value) < Math.abs(best.value - value) ? choice : best, choices[0]!).label;
}

function ChoiceRow({ label, value, choices, onChange }: {
  label: string;
  value: number;
  choices: readonly { label: string; value: number }[];
  onChange: (value: number) => void;
}) {
  const current = nearestLabel(value, choices);
  return <fieldset><legend>{label}</legend><div className="actions">{choices.map(choice => <button key={choice.label} className={current === choice.label ? "selected" : "ghost"} onClick={() => onChange(choice.value)}>{choice.label}</button>)}</div></fieldset>;
}

export function VoiceSettingsSurface() {
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [language, setLanguage] = useState("en");
  const [shared, setShared] = useState<SharedPhrase[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load() {
    try {
      const [prefs, languageResult, sharedResult] = await Promise.all([
        api<Preferences>("/api/v2/brain/mirror-preferences"),
        api<{ preferred_response_language: string | null }>("/api/v2/brain/communication/preferred-language"),
        api<{ shared_language: SharedPhrase[] }>("/api/v2/brain/shared-language"),
      ]);
      setPreferences(prefs);
      setLanguage(languageResult.preferred_response_language ?? "en");
      setShared(sharedResult.shared_language);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not load Mirror Voice settings"); }
  }
  useEffect(() => { void load(); }, []);

  async function patchPreferences(patch: Record<string, unknown>) {
    try {
      const next = await api<Preferences>("/api/v2/brain/mirror-preferences", { method: "PATCH", body: JSON.stringify(patch) });
      setPreferences(next); setMessage("Mirror Voice updated. The underlying claim meaning does not change.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not save preference"); }
  }
  async function saveLanguage() {
    try {
      const result = await api<{ preferred_response_language: string }>("/api/v2/brain/communication/preferred-language", { method: "PUT", body: JSON.stringify({ preferred_response_language: language }) });
      setLanguage(result.preferred_response_language); setMessage("Preferred response language updated.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not save language"); }
  }
  async function sharedVerdict(item: SharedPhrase, verdict: "confirm" | "reject" | "retire" | "forget" | "edit", edits?: { phrase?: string; meaning?: string }) {
    try {
      await api(`/api/v2/brain/shared-language/${encodeURIComponent(item.id)}/verdict`, { method: "POST", body: JSON.stringify({ verdict, ...edits }) });
      setMessage(verdict === "confirm" ? "Phrase confirmed." : verdict === "edit" ? "Phrase meaning updated." : verdict === "forget" ? "Phrase forgotten." : "Phrase will no longer be reused automatically.");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not update phrase"); }
  }
  async function resetAdaptations() {
    try {
      await api("/api/v2/brain/communication/reset-adaptations", { method: "POST", body: "{}" });
      setMessage("Learned communication adaptations were reset. Your autobiographical memory was not erased.");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not reset communication adaptations"); }
  }

  return <main className="flow"><a className="wordmark" href="/today">WORD &amp; WILL</a><section><p className="eyebrow">MIRROR VOICE</p><h1>How should The Mirror speak to you?</h1><p>These controls change wording, density, and cadence. They do not change the semantic claim, your autobiographical evidence, or safety boundaries.</p>{error && <p role="alert">{error}</p>}{preferences && <>
    <fieldset><legend>Register</legend><div className="actions"><button className={preferences.register === "practical" ? "selected" : "ghost"} onClick={() => patchPreferences({ register: "practical" })}>Practical</button><button className={preferences.register === "classic" ? "selected" : "ghost"} onClick={() => patchPreferences({ register: "classic" })}>Classic</button></div></fieldset>
    <ChoiceRow label="Directness" value={preferences.directness} choices={levels.directness} onChange={value => patchPreferences({ directness: value })}/>
    <ChoiceRow label="Challenge" value={preferences.challenge_level} choices={levels.challenge} onChange={value => patchPreferences({ challenge_level: value })}/>
    <ChoiceRow label="Response length" value={preferences.verbosity} choices={levels.verbosity} onChange={value => patchPreferences({ verbosity: value })}/>
    <ChoiceRow label="Evidence detail" value={preferences.evidence_detail} choices={levels.evidence} onChange={value => patchPreferences({ evidence_detail: value })}/>
  </>}

  <label>Preferred response language<select data-testid="preferred-response-language" value={language} onChange={event => setLanguage(event.target.value)}><option value="en">English</option><option value="es">Español</option><option value="es-PR">Español (Puerto Rico)</option></select></label><button className="secondary" onClick={saveLanguage}>Save language</button>

  <section data-testid="shared-language-settings"><h2>Shared Language</h2><p>These are phrases The Mirror has noticed may carry a specific meaning for you. You remain the authority on what they mean and whether they may be reused.</p>{shared.length === 0 && <p>No active Shared Language phrases yet.</p>}{shared.map(item => <SharedLanguageEditor key={item.id} item={item} onVerdict={sharedVerdict}/>)}</section>

  <aside><h2>Reset how The Mirror has learned to speak</h2><p>This resets learned communication adaptations only. It does not delete Wills, Catches, Corrections, Moves, Outcomes, Evidence, or other autobiographical memory.</p><button className="ghost" data-testid="reset-communication-adaptations" onClick={resetAdaptations}>Reset learned communication style</button></aside>
  {message && <p role="status">{message}</p>}<nav className="actions"><a href="/today">Today</a><a href="/memory">Memory</a><a href="/recordings">Recording privacy</a></nav></section></main>;
}

function SharedLanguageEditor({ item, onVerdict }: {
  item: SharedPhrase;
  onVerdict: (item: SharedPhrase, verdict: "confirm" | "reject" | "retire" | "forget" | "edit", edits?: { phrase?: string; meaning?: string }) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false), [phrase, setPhrase] = useState(item.phrase), [meaning, setMeaning] = useState(item.meaning);
  return <article data-testid="shared-language-item"><p className="eyebrow">{item.status === "candidate" ? "POSSIBLE SHARED PHRASE" : "ACTIVE SHARED PHRASE"}</p>{editing ? <><label>Phrase<input value={phrase} onChange={event => setPhrase(event.target.value)}/></label><label>What it means to me<textarea value={meaning} onChange={event => setMeaning(event.target.value)}/></label></> : <><h3>“{item.phrase}”</h3><p>{item.meaning}</p><p className="fine">Reuse: {item.reuse_policy.replaceAll("_", " ")}</p></>}<div className="actions">{editing ? <><button onClick={() => { void onVerdict(item, "edit", { phrase, meaning }); setEditing(false); }}>Save meaning</button><button className="ghost" onClick={() => setEditing(false)}>Cancel</button></> : <><button className="ghost" onClick={() => setEditing(true)}>Edit</button>{item.status === "candidate" && <button onClick={() => onVerdict(item, "confirm")}>Confirm</button>}<button className="ghost" onClick={() => onVerdict(item, item.status === "active" ? "retire" : "reject")}>{item.status === "active" ? "Retire" : "Reject"}</button><button className="ghost" onClick={() => onVerdict(item, "forget")}>Forget</button></>}</div></article>;
}
